import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, RefreshCw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  vendorPortalApi,
  type ClassifyInvoiceBody,
  type HospitalVendor,
  type VendorApproverCandidate,
  type VendorApprovalStep,
  type VendorCategory,
  type VendorInvoice,
  type VendorInvoiceStatus,
} from "@/api/vendorPortal";
import { chainApi } from "@/api/chain";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/utils/dateUtils";
import { Checkbox } from "@/components/ui/checkbox";

function money(n: number) {
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusBadge(status: VendorInvoiceStatus) {
  return <Badge variant="secondary">{status.replace(/_/g, " ")}</Badge>;
}

function categoryLabel(row: VendorInvoice) {
  if (row.expenseLedger === "IPD_PATIENT") {
    return row.patientExpenseCategory?.replace(/_/g, " ") ?? "IPD patient";
  }
  return row.expenseCategory?.replace(/_/g, " ") ?? "—";
}

function serviceMonthValue(iso: string | null | undefined, fallbackIso: string) {
  const d = new Date(iso || fallbackIso);
  if (isNaN(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
}

type MatrixPick = {
  key: string;
  assigneeType: "STAFF" | "ADMIN";
  id: string;
  label: string;
};

function approvalStepLabel(s: VendorApprovalStep): string {
  if (s.assigneeType === "STAFF") {
    const role = s.staff?.role ? ` (${String(s.staff.role).replace(/_/g, " ")})` : "";
    return `${s.staff?.name ?? "Staff"}${role}`;
  }
  if (s.assigneeType === "ADMIN") {
    const scope =
      s.hospitalAdmin?.adminScope === "CHAIN" ? "Chain Admin" : "Hospital Admin";
    return `${s.hospitalAdmin?.name ?? "Admin"} (${scope})`;
  }
  return (s.role ?? "").replace(/_/g, " ") || "Role";
}

function canApproveCurrentStep(
  row: VendorInvoice,
  user: { id?: string; role?: string } | null | undefined
): boolean {
  if (!user?.id) return false;
  const isAdminUser = user.role === "HOSPITAL_ADMIN" || user.role === "SUPER_ADMIN";
  const steps = row.vendor?.approvalSteps ?? [];
  const step = steps[row.currentStepIndex ?? 0];
  if (!step) {
    if (row.status === "PENDING_ADMIN") return isAdminUser;
    if (row.status === "PENDING_OPS") {
      return (
        user.role === "OPERATIONS_MANAGER" ||
        user.role === "BUSINESS_ANALYST" ||
        isAdminUser
      );
    }
    return false;
  }
  if (step.assigneeType === "STAFF") return step.staffId === user.id;
  if (step.assigneeType === "ADMIN") return step.hospitalAdminId === user.id;
  if (step.role === "HOSPITAL_ADMIN" || step.role === "SUPER_ADMIN") return isAdminUser;
  if (step.role && step.role === user.role) return true;
  // Admins may complete role-based intermediate steps (backend allows)
  if (isAdminUser && step.assigneeType === "ROLE") return true;
  return false;
}

export default function VendorManagementPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "HOSPITAL_ADMIN" || user?.role === "SUPER_ADMIN";
  const isOps =
    user?.role === "OPERATIONS_MANAGER" ||
    user?.role === "BUSINESS_ANALYST" ||
    isAdmin;
  const qc = useQueryClient();
  const [tab, setTab] = useState("invoices");

  const { data: meta } = useQuery({
    queryKey: ["vendor-meta"],
    queryFn: async () => (await vendorPortalApi.meta()).data.data,
  });

  const { data: chainHospitals = [] } = useQuery({
    queryKey: ["chain-hospitals-vendor"],
    queryFn: async () => {
      try {
        const r = await chainApi.getGroup();
        const data = r.data?.data as { hospitals?: { id: string; name: string }[] } | undefined;
        return data?.hospitals ?? [];
      } catch {
        return [];
      }
    },
    enabled: isOps,
  });

  const { data: categories = [], refetch: refetchCats } = useQuery({
    queryKey: ["vendor-categories"],
    queryFn: async () =>
      (await vendorPortalApi.listCategories({ includeInactive: isAdmin })).data.data,
    enabled: isOps,
  });

  const { data: vendors = [], refetch: refetchVendors } = useQuery({
    queryKey: ["hospital-vendors"],
    queryFn: async () => (await vendorPortalApi.listVendors()).data.data,
    enabled: isOps,
  });

  const vendorsSorted = useMemo(
    () =>
      [...vendors].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", "en", { sensitivity: "base" })
      ),
    [vendors]
  );

  const { data: approverPool } = useQuery({
    queryKey: ["vendor-approvers"],
    queryFn: async () => (await vendorPortalApi.listApprovers()).data.data,
    enabled: isOps,
  });

  const approverOptions = useMemo(() => {
    const staff = approverPool?.staff ?? [];
    const admins = approverPool?.admins ?? [];
    return [...staff, ...admins] as VendorApproverCandidate[];
  }, [approverPool]);

  const { data: invoices = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ["vendor-invoices-manage"],
    queryFn: async () => (await vendorPortalApi.listInvoices()).data.data,
    enabled: isOps,
  });

  const [catOpen, setCatOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<VendorCategory | null>(null);
  const [catForm, setCatForm] = useState({
    name: "",
    expenseLedger: "CENTRAL",
    defaultExpenseCategory: "",
    defaultPatientExpenseCategory: "",
    isActive: true,
  });

  const [vendorOpen, setVendorOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState<HospitalVendor | null>(null);
  const [vendorForm, setVendorForm] = useState({
    name: "",
    categoryId: "",
    phone: "",
    email: "",
    scope: "HOSPITAL" as string,
    matrixPicks: [] as MatrixPick[],
    isActive: true,
  });
  const [approverToAdd, setApproverToAdd] = useState("");

  const [approveOpen, setApproveOpen] = useState(false);
  const [approveMode, setApproveMode] = useState<"ops" | "admin" | "category">("admin");
  const [approveTarget, setApproveTarget] = useState<VendorInvoice | null>(null);
  const [classify, setClassify] = useState({
    expenseLedger: "CENTRAL",
    expenseCategory: "",
    patientExpenseCategory: "",
    expenseSubCategory: "",
    expenseHospitalId: "",
    hospitalUnit: "",
    serviceMonth: "",
    admissionId: "",
    description: "",
    admissionSearch: "",
  });

  const { data: admissions = [] } = useQuery({
    queryKey: ["vendor-admissions", classify.admissionSearch],
    queryFn: async () =>
      (
        await vendorPortalApi.listAdmissions({
          q: classify.admissionSearch || undefined,
        })
      ).data.data,
    enabled: approveOpen && classify.expenseLedger === "IPD_PATIENT",
  });

  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState<VendorInvoice | null>(null);
  const [payMode, setPayMode] = useState("BANK_TRANSFER");
  const [payAmount, setPayAmount] = useState("");

  const queue = useMemo(() => {
    const ops = invoices.filter((i) => i.status === "PENDING_OPS");
    const admin = invoices.filter((i) => i.status === "PENDING_ADMIN");
    const payable = invoices.filter((i) => i.status === "APPROVED");
    return { ops, admin, payable };
  }, [invoices]);

  const classifyBody = (): ClassifyInvoiceBody => ({
    expenseLedger: classify.expenseLedger,
    expenseCategory:
      classify.expenseLedger === "CENTRAL" ? classify.expenseCategory || undefined : undefined,
    patientExpenseCategory:
      classify.expenseLedger === "IPD_PATIENT"
        ? classify.patientExpenseCategory || undefined
        : undefined,
    expenseSubCategory: classify.expenseSubCategory || undefined,
    expenseHospitalId: classify.expenseHospitalId || undefined,
    hospitalUnit: classify.hospitalUnit || undefined,
    serviceMonth: classify.serviceMonth || undefined,
    admissionId:
      classify.expenseLedger === "IPD_PATIENT" ? classify.admissionId || undefined : undefined,
    description: classify.description || undefined,
  });

  const resetCatForm = () => {
    setEditingCategory(null);
    setCatForm({
      name: "",
      expenseLedger: "CENTRAL",
      defaultExpenseCategory: "",
      defaultPatientExpenseCategory: "",
      isActive: true,
    });
  };

  const resetVendorForm = () => {
    setEditingVendor(null);
    setApproverToAdd("");
    setVendorForm({
      name: "",
      categoryId: "",
      phone: "",
      email: "",
      scope: "HOSPITAL",
      matrixPicks: [],
      isActive: true,
    });
  };

  const openAddCategory = () => {
    resetCatForm();
    setCatOpen(true);
  };

  const openEditCategory = (c: VendorCategory) => {
    setEditingCategory(c);
    setCatForm({
      name: c.name,
      expenseLedger: c.expenseLedger ?? "CENTRAL",
      defaultExpenseCategory: c.defaultExpenseCategory ?? "",
      defaultPatientExpenseCategory: c.defaultPatientExpenseCategory ?? "",
      isActive: c.isActive,
    });
    setCatOpen(true);
  };

  const openAddVendor = () => {
    resetVendorForm();
    setVendorForm((f) => ({ ...f, scope: "HOSPITAL" }));
    setVendorOpen(true);
  };

  const openEditVendor = (v: HospitalVendor) => {
    const picks: MatrixPick[] = (v.approvalSteps ?? [])
      .filter((s) => {
        if (s.assigneeType === "ROLE" && (s.role === "HOSPITAL_ADMIN" || s.role === "SUPER_ADMIN")) {
          return false;
        }
        return true;
      })
      .map((s) => {
        if (s.assigneeType === "STAFF" && s.staffId) {
          return {
            key: `STAFF:${s.staffId}`,
            assigneeType: "STAFF" as const,
            id: s.staffId,
            label: approvalStepLabel(s),
          };
        }
        if (s.assigneeType === "ADMIN" && s.hospitalAdminId) {
          return {
            key: `ADMIN:${s.hospitalAdminId}`,
            assigneeType: "ADMIN" as const,
            id: s.hospitalAdminId,
            label: approvalStepLabel(s),
          };
        }
        // Legacy ROLE pre-steps (e.g. OPERATIONS_MANAGER) — keep label but skip save as role;
        // resolve to first matching staff of that role when possible
        const role = s.role;
        const match = (approverPool?.staff ?? []).find((a) => a.role === role);
        if (match) {
          return {
            key: `STAFF:${match.id}`,
            assigneeType: "STAFF" as const,
            id: match.id,
            label: match.label,
          };
        }
        return null;
      })
      .filter((p): p is MatrixPick => p != null);

    setEditingVendor(v);
    setApproverToAdd("");
    setVendorForm({
      name: v.name,
      categoryId: v.categoryId ?? "",
      phone: v.phone ?? "",
      email: v.email ?? "",
      scope: v.scope ?? "HOSPITAL",
      matrixPicks: picks,
      isActive: v.isActive,
    });
    setVendorOpen(true);
  };

  const approvalStepsPayload = () =>
    vendorForm.matrixPicks.map((p) =>
      p.assigneeType === "STAFF"
        ? { assigneeType: "STAFF", staffId: p.id }
        : { assigneeType: "ADMIN", hospitalAdminId: p.id }
    );

  const createCatMut = useMutation({
    mutationFn: () =>
      vendorPortalApi.createCategory({
        name: catForm.name,
        expenseLedger: catForm.expenseLedger,
        defaultExpenseCategory:
          catForm.expenseLedger === "CENTRAL"
            ? catForm.defaultExpenseCategory || null
            : null,
        defaultPatientExpenseCategory:
          catForm.expenseLedger === "IPD_PATIENT"
            ? catForm.defaultPatientExpenseCategory || null
            : null,
      }),
    onSuccess: () => {
      toast.success("Category created");
      setCatOpen(false);
      resetCatForm();
      void refetchCats();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const updateCatMut = useMutation({
    mutationFn: () =>
      vendorPortalApi.updateCategory(editingCategory!.id, {
        name: catForm.name,
        expenseLedger: catForm.expenseLedger,
        defaultExpenseCategory:
          catForm.expenseLedger === "CENTRAL"
            ? catForm.defaultExpenseCategory || null
            : null,
        defaultPatientExpenseCategory:
          catForm.expenseLedger === "IPD_PATIENT"
            ? catForm.defaultPatientExpenseCategory || null
            : null,
        isActive: catForm.isActive,
      }),
    onSuccess: () => {
      toast.success("Category updated");
      setCatOpen(false);
      resetCatForm();
      void refetchCats();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const createVendorMut = useMutation({
    mutationFn: () =>
      vendorPortalApi.createVendor({
        name: vendorForm.name,
        categoryId: vendorForm.categoryId || null,
        phone: vendorForm.phone || null,
        email: vendorForm.email || null,
        scope: isAdmin ? vendorForm.scope : "HOSPITAL",
        approvalSteps: approvalStepsPayload(),
      }),
    onSuccess: () => {
      toast.success("Vendor created — Hospital Admin is always the final approver");
      setVendorOpen(false);
      resetVendorForm();
      void refetchVendors();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const updateVendorMut = useMutation({
    mutationFn: () =>
      vendorPortalApi.updateVendor(editingVendor!.id, {
        name: vendorForm.name,
        categoryId: vendorForm.categoryId || null,
        phone: vendorForm.phone || null,
        email: vendorForm.email || null,
        scope: isAdmin ? vendorForm.scope : "HOSPITAL",
        isActive: vendorForm.isActive,
        approvalSteps: approvalStepsPayload(),
      }),
    onSuccess: () => {
      toast.success("Vendor updated");
      setVendorOpen(false);
      resetVendorForm();
      void refetchVendors();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const opsApproveMut = useMutation({
    mutationFn: () => vendorPortalApi.approveStep(approveTarget!.id, classifyBody()),
    onSuccess: () => {
      toast.success("Approval step completed");
      setApproveOpen(false);
      void qc.invalidateQueries({ queryKey: ["vendor-invoices-manage"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const setCategoryMut = useMutation({
    mutationFn: () => vendorPortalApi.setExpenseCategory(approveTarget!.id, classifyBody()),
    onSuccess: () => {
      toast.success("Expense fields updated");
      setApproveOpen(false);
      void qc.invalidateQueries({ queryKey: ["vendor-invoices-manage"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      vendorPortalApi.reject(id, reason),
    onSuccess: () => {
      toast.success("Invoice rejected");
      void qc.invalidateQueries({ queryKey: ["vendor-invoices-manage"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const adminApproveMut = useMutation({
    mutationFn: () => vendorPortalApi.adminApprove(approveTarget!.id, classifyBody()),
    onSuccess: () => {
      toast.success("Approved — expense created. Mark paid when settled.");
      setApproveOpen(false);
      void qc.invalidateQueries({ queryKey: ["vendor-invoices-manage"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const openCategoryDialog = (
    row: VendorInvoice,
    mode: "ops" | "admin" | "category"
  ) => {
    setApproveTarget(row);
    setApproveMode(mode);
    const ledger =
      row.expenseLedger ||
      row.vendor?.category?.expenseLedger ||
      "CENTRAL";
    setClassify({
      expenseLedger: ledger,
      expenseCategory:
        row.expenseCategory ||
        row.vendor?.category?.defaultExpenseCategory ||
        meta?.expenseCategories?.[0] ||
        "",
      patientExpenseCategory:
        row.patientExpenseCategory ||
        row.vendor?.category?.defaultPatientExpenseCategory ||
        "",
      expenseSubCategory: row.expenseSubCategory || "",
      expenseHospitalId: row.expenseHospitalId || row.hospitalId || "",
      hospitalUnit:
        row.hospitalUnit ||
        (row.vendor?.scope === "CENTRAL" ? "CENTRAL" : "") ||
        "",
      serviceMonth: serviceMonthValue(row.serviceMonth, row.invoiceDate),
      admissionId: row.admissionId || "",
      description: row.description || "",
      admissionSearch: "",
    });
    setApproveOpen(true);
  };

  const markPaidMut = useMutation({
    mutationFn: () => {
      const paidAmount = payAmount.trim() ? Number(payAmount) : undefined;
      return vendorPortalApi.markPaid(payTarget!.id, {
        paymentMode: payMode,
        paidAmount,
      });
    },
    onSuccess: () => {
      toast.success("Marked paid");
      setPayOpen(false);
      void qc.invalidateQueries({ queryKey: ["vendor-invoices-manage"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || "Failed"),
  });

  const addMatrixApprover = (value: string) => {
    if (!value || value === "__none__") return;
    const opt = approverOptions.find((o) => `${o.kind}:${o.id}` === value);
    if (!opt) return;
    const key = `${opt.kind}:${opt.id}`;
    setVendorForm((f) => {
      if (f.matrixPicks.some((p) => p.key === key)) return f;
      return {
        ...f,
        matrixPicks: [
          ...f.matrixPicks,
          {
            key,
            assigneeType: opt.kind,
            id: opt.id,
            label: opt.label,
          },
        ],
      };
    });
    setApproverToAdd("");
  };

  const removeMatrixApprover = (key: string) => {
    setVendorForm((f) => ({
      ...f,
      matrixPicks: f.matrixPicks.filter((p) => p.key !== key),
    }));
  };

  const matrixPreviewLabels = [
    ...vendorForm.matrixPicks.map((p) => p.label),
    "Hospital Admin (always)",
  ];

  const availableApprovers = approverOptions.filter(
    (o) => !vendorForm.matrixPicks.some((p) => p.key === `${o.kind}:${o.id}`)
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Vendor payments</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Onboard vendors with hospital/central scope and approval matrix. First-level
            approvers classify expenses into Central or IPD headers; payment syncs to the
            vendor portal.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Awaiting ops / matrix</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-amber-800">
            {queue.ops.length}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Awaiting admin</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-sky-800">
            {queue.admin.length}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Approved — pay</CardTitle>
          </CardHeader>
          <CardContent className="text-2xl font-semibold text-violet-800">
            {queue.payable.length}
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
          {isOps && <TabsTrigger value="vendors">Vendors</TabsTrigger>}
          {isAdmin && <TabsTrigger value="categories">Categories</TabsTrigger>}
        </TabsList>

        <TabsContent value="invoices" className="mt-4 space-y-4">
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <InvoiceQueue
              title="First-level / ops approval (classify expense)"
              rows={queue.ops}
              empty="No invoices waiting for operations / matrix step."
              actions={(row) => (
                <>
                  {canApproveCurrentStep(row, user) && (
                    <Button
                      size="sm"
                      onClick={() => openCategoryDialog(row, "ops")}
                      disabled={opsApproveMut.isPending}
                    >
                      Approve & classify
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openCategoryDialog(row, "category")}
                  >
                    Edit fields
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const reason = window.prompt("Rejection reason") || undefined;
                      rejectMut.mutate({ id: row.id, reason });
                    }}
                  >
                    Reject
                  </Button>
                </>
              )}
            />
          )}

          <InvoiceQueue
            title="Admin approval (final → create expense)"
            rows={queue.admin}
            empty="No invoices waiting for admin."
            actions={(row) =>
              canApproveCurrentStep(row, user) ? (
                <>
                  <Button size="sm" onClick={() => openCategoryDialog(row, "admin")}>
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => openCategoryDialog(row, "category")}
                  >
                    Edit fields
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const reason = window.prompt("Rejection reason") || undefined;
                      rejectMut.mutate({ id: row.id, reason });
                    }}
                  >
                    Reject
                  </Button>
                </>
              ) : (
                <span className="text-xs text-muted-foreground">Waiting for assigned approver</span>
              )
            }
          />

          <InvoiceQueue
            title="Ready to pay"
            rows={queue.payable}
            empty="No approved invoices awaiting payment."
            actions={(row) => (
              <Button
                size="sm"
                onClick={() => {
                  setPayTarget(row);
                  setPayMode("BANK_TRANSFER");
                  setPayAmount(String(row.amount));
                  setPayOpen(true);
                }}
              >
                Mark paid
              </Button>
            )}
          />

          <InvoiceQueue title="All recent" rows={invoices} empty="No vendor invoices." showAll />
        </TabsContent>

        {isOps && (
          <TabsContent value="vendors" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={openAddVendor}>
                Add vendor
              </Button>
            </div>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Onboarded</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Scope</TableHead>
                    <TableHead>Approval matrix</TableHead>
                    <TableHead>Login</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendorsSorted.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell className="font-medium">{v.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {v.createdAt ? formatDate(v.createdAt) : "—"}
                      </TableCell>
                      <TableCell>{v.category?.name ?? "—"}</TableCell>
                      <TableCell>{(v.scope ?? "HOSPITAL").replace(/_/g, " ")}</TableCell>
                      <TableCell className="text-xs max-w-[280px]">
                        {(v.approvalSteps ?? []).map((s) => approvalStepLabel(s)).join(" → ") ||
                          (v.requiresOpsApproval ? "Ops → Admin" : "Admin")}
                      </TableCell>
                      <TableCell className="text-sm">
                        {v.staff ? `${v.staff.name} (${v.staff.email})` : "Not linked"}
                      </TableCell>
                      <TableCell>{v.isActive ? "Yes" : "No"}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => openEditVendor(v)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="categories" className="mt-4 space-y-3">
            <div className="flex justify-end">
              <Button size="sm" onClick={openAddCategory}>
                Add category
              </Button>
            </div>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Ledger</TableHead>
                    <TableHead>Default expense header</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {categories.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{(c.expenseLedger ?? "CENTRAL").replace(/_/g, " ")}</TableCell>
                      <TableCell>
                        {c.expenseLedger === "IPD_PATIENT"
                          ? c.defaultPatientExpenseCategory?.replace(/_/g, " ") ?? "—"
                          : c.defaultExpenseCategory?.replace(/_/g, " ") ?? "—"}
                      </TableCell>
                      <TableCell>{c.isActive ? "Yes" : "No"}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => openEditCategory(c)}>
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      <Dialog
        open={catOpen}
        onOpenChange={(open) => {
          setCatOpen(open);
          if (!open) resetCatForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? "Edit vendor category" : "Add vendor category"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={catForm.name}
                onChange={(e) => setCatForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Expense ledger (auto-route)</Label>
              <Select
                value={catForm.expenseLedger}
                onValueChange={(v) => setCatForm((f) => ({ ...f, expenseLedger: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CENTRAL">Central expenses</SelectItem>
                  <SelectItem value="IPD_PATIENT">IPD patient expenses</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {catForm.expenseLedger === "CENTRAL" ? (
              <div>
                <Label>Default central expense category</Label>
                <Select
                  value={catForm.defaultExpenseCategory || "__none__"}
                  onValueChange={(v) =>
                    setCatForm((f) => ({
                      ...f,
                      defaultExpenseCategory: v === "__none__" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {(meta?.expenseCategories ?? []).map((c) => (
                      <SelectItem key={c} value={c}>
                        {c.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label>Default IPD patient expense category</Label>
                <Select
                  value={catForm.defaultPatientExpenseCategory || "__none__"}
                  onValueChange={(v) =>
                    setCatForm((f) => ({
                      ...f,
                      defaultPatientExpenseCategory: v === "__none__" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Optional" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {(meta?.patientExpenseCategories ?? []).map((c) => (
                      <SelectItem key={c} value={c}>
                        {c.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editingCategory && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="cat-active"
                  checked={catForm.isActive}
                  onCheckedChange={(c) =>
                    setCatForm((f) => ({ ...f, isActive: c === true }))
                  }
                />
                <Label htmlFor="cat-active">Active</Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCatOpen(false);
                resetCatForm();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                createCatMut.isPending ||
                updateCatMut.isPending ||
                !catForm.name.trim()
              }
              onClick={() =>
                editingCategory ? updateCatMut.mutate() : createCatMut.mutate()
              }
            >
              {editingCategory ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={vendorOpen}
        onOpenChange={(open) => {
          setVendorOpen(open);
          if (!open) resetVendorForm();
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVendor ? "Edit vendor" : "Add vendor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input
                value={vendorForm.name}
                onChange={(e) => setVendorForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select
                value={vendorForm.categoryId || "__none__"}
                onValueChange={(v) =>
                  setVendorForm((f) => ({ ...f, categoryId: v === "__none__" ? "" : v }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {categories
                    .filter(
                      (c) =>
                        c.isActive ||
                        c.id === vendorForm.categoryId ||
                        c.id === editingVendor?.categoryId
                    )
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} ({(c.expenseLedger ?? "CENTRAL").replace(/_/g, " ")})
                        {!c.isActive ? " — inactive" : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor scope</Label>
              {isAdmin ? (
                <Select
                  value={vendorForm.scope}
                  onValueChange={(v) => setVendorForm((f) => ({ ...f, scope: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HOSPITAL">This hospital</SelectItem>
                    <SelectItem value="ALL_HOSPITALS">Common for all hospitals</SelectItem>
                    <SelectItem value="CENTRAL">Central / chain office</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <>
                  <Input value="This hospital" disabled />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ops / Business Analyst can only add vendors for their own hospital.
                  </p>
                </>
              )}
            </div>
            <div>
              <Label>Approval matrix</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Add one or more named approvers (hospital staff or chain/hospital admins).
                Hospital Admin remains the final approver for every vendor.
              </p>
              <div className="flex flex-wrap gap-2 mb-2">
                {vendorForm.matrixPicks.map((p) => (
                  <Badge key={p.key} variant="secondary" className="gap-1 pr-1">
                    {p.label}
                    <button
                      type="button"
                      className="rounded-sm p-0.5 hover:bg-muted"
                      aria-label={`Remove ${p.label}`}
                      onClick={() => removeMatrixApprover(p.key)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Badge variant="outline" className="self-center">
                  Hospital Admin (always)
                </Badge>
              </div>
              <div className="flex gap-2 items-center">
                <Select
                  value={approverToAdd || "__none__"}
                  onValueChange={(v) => {
                    if (v === "__none__") {
                      setApproverToAdd("");
                      return;
                    }
                    addMatrixApprover(v);
                  }}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Add approver…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Add approver…</SelectItem>
                    {availableApprovers.map((o) => (
                      <SelectItem key={`${o.kind}:${o.id}`} value={`${o.kind}:${o.id}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={vendorForm.matrixPicks.length === 0}
                  onClick={() => setVendorForm((f) => ({ ...f, matrixPicks: [] }))}
                >
                  Admin only
                </Button>
              </div>
              <p className="text-xs mt-2">Flow: {matrixPreviewLabels.join(" → ")}</p>
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={vendorForm.phone}
                onChange={(e) => setVendorForm((f) => ({ ...f, phone: e.target.value }))}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                value={vendorForm.email}
                onChange={(e) => setVendorForm((f) => ({ ...f, email: e.target.value }))}
              />
            </div>
            {editingVendor && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="vendor-active"
                  checked={vendorForm.isActive}
                  onCheckedChange={(c) =>
                    setVendorForm((f) => ({ ...f, isActive: c === true }))
                  }
                />
                <Label htmlFor="vendor-active">Active</Label>
              </div>
            )}
            {editingVendor?.staff && (
              <p className="text-xs text-muted-foreground">
                Login linked: {editingVendor.staff.name} ({editingVendor.staff.email}). Link
                or change login from User Management.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setVendorOpen(false);
                resetVendorForm();
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={
                createVendorMut.isPending ||
                updateVendorMut.isPending ||
                !vendorForm.name.trim()
              }
              onClick={() =>
                editingVendor ? updateVendorMut.mutate() : createVendorMut.mutate()
              }
            >
              {editingVendor ? "Update" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {approveMode === "ops"
                ? "Classify & approve (first level)"
                : approveMode === "category"
                  ? "Edit expense classification"
                  : "Admin approve — create expense"}
            </DialogTitle>
          </DialogHeader>
          {approveTarget && (
            <div className="space-y-3">
              <p className="text-sm">
                {approveTarget.vendor?.name} · {approveTarget.invoiceNumber} ·{" "}
                {money(approveTarget.amount)}
              </p>
              <div>
                <Label>Expense ledger</Label>
                <Select
                  value={classify.expenseLedger}
                  onValueChange={(v) => setClassify((f) => ({ ...f, expenseLedger: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CENTRAL">Central expenses</SelectItem>
                    <SelectItem value="IPD_PATIENT">IPD patient expenses</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {classify.expenseLedger === "CENTRAL" ? (
                <div>
                  <Label>Expense category (header)</Label>
                  <Select
                    value={classify.expenseCategory || "__none__"}
                    onValueChange={(v) =>
                      setClassify((f) => ({
                        ...f,
                        expenseCategory: v === "__none__" ? "" : v,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(meta?.expenseCategories ?? []).map((c) => (
                        <SelectItem key={c} value={c}>
                          {c.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <>
                  <div>
                    <Label>IPD patient expense category</Label>
                    <Select
                      value={classify.patientExpenseCategory || "__none__"}
                      onValueChange={(v) =>
                        setClassify((f) => ({
                          ...f,
                          patientExpenseCategory: v === "__none__" ? "" : v,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(meta?.patientExpenseCategories ?? []).map((c) => (
                          <SelectItem key={c} value={c}>
                            {c.replace(/_/g, " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Search admission</Label>
                    <Input
                      placeholder="Patient name / UHID / phone"
                      value={classify.admissionSearch}
                      onChange={(e) =>
                        setClassify((f) => ({ ...f, admissionSearch: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>IPD admission</Label>
                    <Select
                      value={classify.admissionId || "__none__"}
                      onValueChange={(v) =>
                        setClassify((f) => ({
                          ...f,
                          admissionId: v === "__none__" ? "" : v,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select admission" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Select…</SelectItem>
                        {admissions.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.queue.patient.name}
                            {a.queue.patient.uhid ? ` · ${a.queue.patient.uhid}` : ""} ·{" "}
                            {a.status}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label>Hospital</Label>
                <Select
                  value={classify.expenseHospitalId || "__none__"}
                  onValueChange={(v) =>
                    setClassify((f) => ({
                      ...f,
                      expenseHospitalId: v === "__none__" ? "" : v,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Hospital" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Default / actor hospital</SelectItem>
                    {(Array.isArray(chainHospitals) ? chainHospitals : []).map(
                      (h: { id: string; name: string }) => (
                        <SelectItem key={h.id} value={h.id}>
                          {h.name}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              {classify.expenseLedger === "CENTRAL" && (
                <div>
                  <Label>Hospital unit / service site</Label>
                  <Select
                    value={classify.hospitalUnit || "__none__"}
                    onValueChange={(v) =>
                      setClassify((f) => ({
                        ...f,
                        hospitalUnit: v === "__none__" ? "" : v,
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Auto from hospital</SelectItem>
                      {(meta?.hospitalUnits ?? []).map((u) => (
                        <SelectItem key={u} value={u}>
                          {u.replace(/_/g, " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Service month</Label>
                <Input
                  type="month"
                  value={classify.serviceMonth}
                  onChange={(e) =>
                    setClassify((f) => ({ ...f, serviceMonth: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Sub-category (optional)</Label>
                <Input
                  value={classify.expenseSubCategory}
                  onChange={(e) =>
                    setClassify((f) => ({ ...f, expenseSubCategory: e.target.value }))
                  }
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={classify.description}
                  onChange={(e) =>
                    setClassify((f) => ({ ...f, description: e.target.value }))
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>
              Cancel
            </Button>
            {approveMode === "ops" ? (
              <Button disabled={opsApproveMut.isPending} onClick={() => opsApproveMut.mutate()}>
                Approve step
              </Button>
            ) : approveMode === "category" ? (
              <Button disabled={setCategoryMut.isPending} onClick={() => setCategoryMut.mutate()}>
                Save fields
              </Button>
            ) : (
              <Button disabled={adminApproveMut.isPending} onClick={() => adminApproveMut.mutate()}>
                Approve & create expense
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark vendor invoice paid</DialogTitle>
          </DialogHeader>
          {payTarget && (
            <div className="space-y-3">
              <p className="text-sm">
                {payTarget.vendor?.name} · {payTarget.invoiceNumber} · {money(payTarget.amount)}
              </p>
              <div>
                <Label>Payment mode</Label>
                <Select value={payMode} onValueChange={setPayMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(meta?.paymentModes ?? ["CASH", "UPI", "BANK_TRANSFER", "CHEQUE"]).map(
                      (m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Paid amount</Label>
                <Input
                  type="number"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>
              Cancel
            </Button>
            <Button disabled={markPaidMut.isPending} onClick={() => markPaidMut.mutate()}>
              Confirm paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InvoiceQueue({
  title,
  rows,
  empty,
  actions,
  showAll,
}: {
  title: string;
  rows: VendorInvoice[];
  empty: string;
  actions?: (row: VendorInvoice) => React.ReactNode;
  showAll?: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">
          {title}{" "}
          <Badge variant="secondary" className="ml-2">
            {rows.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{empty}</p>
        ) : (
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Vendor</TableHead>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Category</TableHead>
                  {showAll && <TableHead>Status</TableHead>}
                  <TableHead>File</TableHead>
                  {actions && <TableHead className="text-right">Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.vendor?.name ?? "—"}</TableCell>
                    <TableCell>{row.invoiceNumber}</TableCell>
                    <TableCell>{formatDate(row.invoiceDate)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(row.amount)}</TableCell>
                    <TableCell className="text-xs">{categoryLabel(row)}</TableCell>
                    {showAll && <TableCell>{statusBadge(row.status)}</TableCell>}
                    <TableCell>
                      {row.fileUrl ? (
                        <a
                          className="text-sky-700 underline text-sm"
                          href={row.fileUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    {actions && (
                      <TableCell className="text-right space-x-2 whitespace-nowrap">
                        {actions(row)}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
